import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Users,
  ArrowLeft,
  LogOut,
  Settings,
  Pencil,
  Key,
  RefreshCw,
  Crown,
  User,
  MoreHorizontal,
  UserMinus,
  Shield,
  Trash2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  ShoppingCart,
  RotateCcw,
} from "lucide-react";
import { InviteManagement } from "./InviteManagement";
import { TokenDisplayModal } from "./TokenDisplayModal";
import { ErrorModal } from "./ErrorModal";
import {
  KickMemberModal,
  TransferAdminModal,
  DisbandGroupModal,
  LeaveGroupModal,
  RenameGroupModal,
} from "./AdminActionModals";
import { formatLocalDateTime, formatRelativeTime } from "@/lib/date-time";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface User {
  codicePersona: string;
  displayName: string;
}

interface Group {
  id: string;
  name: string;
  createdAt: string;
  createdBy: string;
  memberCount?: number;
  userRole?: "admin" | "member";
}

interface GroupMember {
  codice_persona: number;
  display_name: string;
  role: "admin" | "member";
  joined_at: string;
  polimi_expires_at?: string;
  welfare_last_refresh?: string;
}

interface TokenResponse {
  token: string;
  generatedAt: string;
  expiresAt?: string;
}

interface ConsumptionData {
  entries: Array<{
    date: string;
    time: string;
    quantity: number;
    price: string;
    merchant: string;
  }>;
  usedToday: boolean;
  lastUpdated: string;
}

interface MemberConsumption {
  codice_persona: number;
  display_name: string;
  sessionActive: boolean;
  consumptionData: ConsumptionData | null;
  usedToday: boolean;
  error?: string;
  lastUpdated: string;
}

interface GroupViewProps {
  group: Group;
  user: User;
  onLeaveGroup: () => void;
  onGroupUpdated?: (group: Group) => void;
}

export function GroupView({ group, user, onLeaveGroup, onGroupUpdated }: GroupViewProps) {
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [groupName, setGroupName] = useState(group.name);
  const [isGeneratingToken, setIsGeneratingToken] = useState<string | null>(null);
  const [showInviteManagement, setShowInviteManagement] = useState(false);
  const [tokenData, setTokenData] = useState<TokenResponse | null>(null);
  const [refreshingWelfareFor, setRefreshingWelfareFor] = useState<number | null>(null);
  const [consumptionData, setConsumptionData] = useState<MemberConsumption[]>([]);
  const [isLoadingConsumption, setIsLoadingConsumption] = useState(false);
  const [refreshingConsumption, setRefreshingConsumption] = useState(false);
  const [refreshingAllSessions, setRefreshingAllSessions] = useState(false);

  // Admin action modals
  const [kickModalOpen, setKickModalOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [disbandModalOpen, setDisbandModalOpen] = useState(false);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<GroupMember | null>(null);
  const [isPerformingAdminAction, setIsPerformingAdminAction] = useState(false);
  const [isRenamingGroup, setIsRenamingGroup] = useState(false);
  const [isLeavingGroup, setIsLeavingGroup] = useState(false);

  // Error modal states
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [retryAction, setRetryAction] = useState<(() => void) | null>(null);
  const [showRefreshDropdown, setShowRefreshDropdown] = useState(false);

  useEffect(() => {
    fetchGroupMembers();
  }, [group.id]);

  useEffect(() => {
    setGroupName(group.name);
  }, [group.id, group.name]);

  useEffect(() => {
    // Only fetch consumption data on initial mount, not when members change
    if (members.length > 0 && consumptionData.length === 0) {
      fetchConsumptionData();
    }
  }, [members.length]);

  const fetchGroupMembers = async () => {
    try {
      const response = await fetch(`/api/groups/${group.id}`);
      if (response.ok) {
        const data = await response.json();
        setMembers(data.group?.members || []);
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.error || "Failed to fetch group members");
        setRetryAction(() => fetchGroupMembers);
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error fetching group members:", error);
      setErrorMessage("Network error while fetching group members");
      setRetryAction(() => fetchGroupMembers);
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchConsumptionData = async () => {
    if (refreshingConsumption) return;

    setRefreshingConsumption(true);
    try {
      const response = await fetch(`/api/groups/${group.id}/consumption`);
      if (response.ok) {
        const data = await response.json();
        setConsumptionData(data.consumption || []);
      } else {
        const errorData = await response.json();
        console.error("Failed to fetch consumption data:", errorData.error);
        setErrorMessage(errorData.error || "Failed to fetch consumption data");
        setRetryAction(() => fetchConsumptionData);
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error fetching consumption data:", error);
      setErrorMessage("Network error while fetching consumption data");
      setRetryAction(() => fetchConsumptionData);
      setShowErrorModal(true);
    } finally {
      setRefreshingConsumption(false);
    }
  };

  const handleGenerateToken = async (targetCodicePersona: number) => {
    setIsGeneratingToken(targetCodicePersona.toString());
    try {
      const response = await fetch("/api/tokens/generate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          codice_persona: targetCodicePersona,
          group_id: Number(group.id),
        }),
      });

      if (response.ok) {
        const tokenResponse = await response.json();
        setTokenData(tokenResponse);
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.error || "Failed to generate token");
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error generating token:", error);
      setErrorMessage("Network error while generating token");
      setShowErrorModal(true);
    } finally {
      setIsGeneratingToken(null);
    }
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setErrorMessage("");
    setRetryAction(null);
  };

  const handleRefreshWelfareSession = async (memberCodicePersona: number) => {
    setRefreshingWelfareFor(memberCodicePersona);
    try {
      const response = await fetch(`/api/groups/${group.id}/members/${memberCodicePersona}/refresh-welfare`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Update the member's welfare last refresh time in the local state
          setMembers((prevMembers) =>
            prevMembers.map((member) =>
              member.codice_persona === memberCodicePersona
                ? { ...member, welfare_last_refresh: data.welfareLastRefresh }
                : member
            )
          );
        }
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.error || "Failed to refresh welfare session");
        setRetryAction(() => () => handleRefreshWelfareSession(memberCodicePersona));
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error refreshing welfare session:", error);
      setErrorMessage("Network error while refreshing welfare session");
      setRetryAction(() => () => handleRefreshWelfareSession(memberCodicePersona));
      setShowErrorModal(true);
    } finally {
      setRefreshingWelfareFor(null);
    }
  };

  const getSessionInfo = (expiresAt?: string) => {
    if (!expiresAt) return null;

    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    return {
      expiryDate,
      daysUntilExpiry,
      isExpired: daysUntilExpiry < 0,
      isWarning: daysUntilExpiry >= 0 && daysUntilExpiry <= 3,
      daysText:
        daysUntilExpiry < 0
          ? `Expired ${Math.abs(daysUntilExpiry)} days ago`
          : daysUntilExpiry === 0
          ? "Expires today"
          : daysUntilExpiry === 1
          ? "Expires tomorrow"
          : `Expires in ${daysUntilExpiry} days`,
    };
  };

  const getMemberConsumption = (memberCodicePersona: number) => {
    return consumptionData.find((c) => c.codice_persona === memberCodicePersona);
  };

  const handleRefreshAllSessions = async () => {
    setRefreshingAllSessions(true);

    try {
      // Refresh welfare sessions for all members in parallel
      const refreshPromises = members.map(async (member) => {
        if (!member.codice_persona) return null;

        try {
          const response = await fetch(`/api/groups/${group.id}/members/${member.codice_persona}/refresh-welfare`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              return {
                codice_persona: member.codice_persona,
                welfare_last_refresh: data.welfareLastRefresh,
              };
            }
          }
          return null;
        } catch (error) {
          console.error(`Error refreshing session for member ${member.codice_persona}:`, error);
          return null;
        }
      });

      const results = await Promise.allSettled(refreshPromises);
      const successfulRefreshes = results
        .filter(
          (result): result is PromiseFulfilledResult<{ codice_persona: number; welfare_last_refresh: string } | null> =>
            result.status === "fulfilled"
        )
        .map((result) => result.value)
        .filter((result) => result !== null);

      // Update local state with refreshed times
      if (successfulRefreshes.length > 0) {
        setMembers((prevMembers) =>
          prevMembers.map((member) => {
            const refreshed = successfulRefreshes.find((r) => r!.codice_persona === member.codice_persona);
            return refreshed ? { ...member, welfare_last_refresh: refreshed.welfare_last_refresh } : member;
          })
        );
      }

      // Show success message or error message based on results
      if (successfulRefreshes.length === 0) {
        setErrorMessage("Failed to refresh any welfare sessions");
        setRetryAction(() => handleRefreshAllSessions);
        setShowErrorModal(true);
      } else if (successfulRefreshes.length < members.length) {
        console.log(`Refreshed ${successfulRefreshes.length} out of ${members.length} sessions`);
      }
    } catch (error) {
      console.error("Error refreshing all sessions:", error);
      setErrorMessage("Network error while refreshing sessions");
      setRetryAction(() => handleRefreshAllSessions);
      setShowErrorModal(true);
    } finally {
      setRefreshingAllSessions(false);
    }
  };

  const handleLeaveGroupFromModal = async () => {
    setIsLeavingGroup(true);
    try {
      const response = await fetch(`/api/groups/${group.id}/leave`, {
        method: "POST",
      });

      if (response.ok) {
        setLeaveModalOpen(false);
        onLeaveGroup();
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.error || "Failed to leave group");
        setShowErrorModal(true);
        setLeaveModalOpen(false);
      }
    } catch (error) {
      console.error("Error leaving group:", error);
      setErrorMessage("An error occurred while leaving the group");
      setShowErrorModal(true);
      setLeaveModalOpen(false);
    } finally {
      setIsLeavingGroup(false);
    }
  };

  const handleKickMember = async (memberCodicePersona: number) => {
    setIsPerformingAdminAction(true);
    try {
      const response = await fetch(`/api/groups/${group.id}/kick`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ memberCodicePersona }),
      });

      if (response.ok) {
        setKickModalOpen(false);
        fetchGroupMembers(); // Refresh member list
      } else {
        const error = await response.json();
        console.error("Failed to kick member:", error.error);
      }
    } catch (error) {
      console.error("Error kicking member:", error);
    } finally {
      setIsPerformingAdminAction(false);
      setSelectedMember(null);
    }
  };

  const handleTransferAdmin = async (newAdminCodicePersona: number) => {
    setIsPerformingAdminAction(true);
    try {
      const response = await fetch(`/api/groups/${group.id}/transfer-admin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ newAdminCodicePersona }),
      });

      if (response.ok) {
        setTransferModalOpen(false);
        fetchGroupMembers(); // Refresh member list to show new admin
      } else {
        const error = await response.json();
        console.error("Failed to transfer admin:", error.error);
      }
    } catch (error) {
      console.error("Error transferring admin:", error);
    } finally {
      setIsPerformingAdminAction(false);
      setSelectedMember(null);
    }
  };

  const handleRenameGroup = async (newGroupName: string) => {
    setIsRenamingGroup(true);
    try {
      const response = await fetch(`/api/groups/${group.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: newGroupName }),
      });

      if (response.ok) {
        const data = await response.json();
        const updatedName = data.group?.name || newGroupName;
        const updatedGroup = { ...group, name: updatedName };

        setGroupName(updatedName);
        onGroupUpdated?.(updatedGroup);
        setRenameModalOpen(false);
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.error || "Failed to rename group");
        setRetryAction(() => () => handleRenameGroup(newGroupName));
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error renaming group:", error);
      setErrorMessage("Network error while renaming group");
      setRetryAction(() => () => handleRenameGroup(newGroupName));
      setShowErrorModal(true);
    } finally {
      setIsRenamingGroup(false);
    }
  };

  const handleDisbandGroup = async () => {
    setIsPerformingAdminAction(true);
    try {
      const response = await fetch(`/api/groups/${group.id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setDisbandModalOpen(false);
        onLeaveGroup(); // Return to dashboard
      } else {
        const error = await response.json();
        console.error("Failed to disband group:", error.error);
      }
    } catch (error) {
      console.error("Error disbanding group:", error);
    } finally {
      setIsPerformingAdminAction(false);
    }
  };

  const isAdmin = group.userRole === "admin";
  const currentUserIsAdmin = members.find((m) => m.codice_persona === parseInt(user.codicePersona))?.role === "admin";

  // Get the latest consumption data update time
  const getLastConsumptionUpdate = () => {
    if (consumptionData.length === 0) return null;
    return consumptionData.reduce((latest, current) =>
      new Date(current.lastUpdated) > new Date(latest.lastUpdated) ? current : latest
    ).lastUpdated;
  };

  return (
    <div className="min-h-screen w-full min-w-0 overflow-x-clip bg-background">
      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-3 py-3 sm:px-4 sm:py-4">
          <div className="flex flex-wrap items-center gap-2 sm:justify-between">
            <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-4">
              <Button variant="ghost" size="sm" onClick={onLeaveGroup} className="shrink-0 px-2 sm:px-3">
                <ArrowLeft className="h-4 w-4" />
                <span>
                  Back<span className="hidden sm:inline"> to Dashboard</span>
                </span>
              </Button>
              <div className="hidden h-4 w-px bg-border sm:block"></div>
              <div className="flex min-w-0 items-center gap-2">
                <Users className="hidden h-5 w-5 shrink-0 text-primary sm:block" />
                <div className="min-w-0">
                  <h1 className="truncate text-base font-semibold leading-tight sm:text-xl">{groupName}</h1>
                  <p className="truncate text-xs leading-tight text-muted-foreground sm:text-sm">
                    {members.length} member{members.length !== 1 ? "s" : ""}
                    {consumptionData.length > 0 && (
                      <>
                        {" • "}
                        <span className="text-xs">
                          Usage data updated:{" "}
                          {getLastConsumptionUpdate()
                            ? new Date(getLastConsumptionUpdate()!).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "Unknown"}
                        </span>
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-1.5 sm:gap-2">
              <DropdownMenu open={showRefreshDropdown} onOpenChange={setShowRefreshDropdown}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 px-2 sm:px-3"
                    disabled={refreshingAllSessions || refreshingConsumption}
                  >
                    {refreshingAllSessions || refreshingConsumption ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        <span className="hidden sm:inline">Refreshing...</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        <span>Refresh</span>
                      </>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => {
                      setShowRefreshDropdown(false);
                      setTimeout(() => handleRefreshAllSessions(), 100); // Small delay to ensure dropdown closes first
                    }}
                    disabled={refreshingAllSessions}
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Refresh Sessions
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setShowRefreshDropdown(false);
                      setTimeout(() => fetchConsumptionData(), 100); // Small delay to ensure dropdown closes first
                    }}
                    disabled={refreshingConsumption}
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    Refresh Usage
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {currentUserIsAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setRenameModalOpen(true)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Rename Group
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowInviteManagement(true)}>
                      <Settings className="h-4 w-4 mr-2" />
                      Manage Invites
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setDisbandModalOpen(true)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Disband Group
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {!currentUserIsAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setLeaveModalOpen(true)}
                  className="shrink-0 px-2 sm:px-3"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline">Leave Group</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto w-full max-w-full px-3 py-6 sm:px-4 sm:py-8">
        <div className="mx-auto min-w-0 max-w-4xl">
          {/* Group Info */}
          <Card className="mb-6 overflow-hidden sm:mb-8">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Group Members
              </CardTitle>
              <CardDescription>Generate welfare tokens for any member of your group</CardDescription>
            </CardHeader>
            <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-muted-foreground">Loading members...</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {members.filter(Boolean).map((member, index) => (
                    <div
                      key={member.codice_persona || `member-${index}`}
                      className="relative flex min-w-0 flex-col gap-4 rounded-lg border bg-card p-4 transition-colors hover:bg-muted/50 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex min-w-0 items-start gap-3 pr-10 sm:pr-0">
                        <Avatar className="h-10 w-10 shrink-0">
                          <AvatarFallback
                            className={member.role === "admin" ? "bg-primary/10 text-primary" : "bg-muted"}
                          >
                            {member.role === "admin" ? <Crown className="h-4 w-4" /> : <User className="h-4 w-4" />}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="min-w-0 break-words font-medium">{member.display_name}</p>
                          </div>
                          {member.codice_persona && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span>{member.codice_persona}</span>
                            </div>
                          )}

                          {/* Session Information */}
                          <div className="space-y-1 mt-2">
                            {/* Polimi Session Expiry */}
                            {member.polimi_expires_at &&
                              (() => {
                                const sessionInfo = getSessionInfo(member.polimi_expires_at);
                                if (sessionInfo) {
                                  return (
                                    <div className="flex min-w-0 items-start gap-2 text-xs">
                                      {sessionInfo.isExpired ? (
                                        <AlertTriangle className="h-3 w-3 shrink-0 text-red-500" />
                                      ) : sessionInfo.isWarning ? (
                                        <AlertTriangle className="h-3 w-3 shrink-0 text-yellow-500" />
                                      ) : (
                                        <div className="h-3 w-3 shrink-0 rounded-full bg-green-500" />
                                      )}
                                      <span
                                        className={
                                          sessionInfo.isExpired
                                            ? "min-w-0 break-words text-red-600"
                                            : sessionInfo.isWarning
                                            ? "min-w-0 break-words text-yellow-600"
                                            : "min-w-0 break-words text-green-600"
                                        }
                                      >
                                        Polimi: {sessionInfo.daysText}
                                      </span>
                                    </div>
                                  );
                                }
                                return null;
                              })()}

                            {/* Welfare Session Info */}
                            <div className="flex w-full min-w-0 items-start gap-2 text-xs">
                              <div className="h-3 w-3 shrink-0 rounded-full bg-blue-500" />
                              <span className="min-w-0 flex-1 break-words text-blue-600">
                                Welfare:{" "}
                                {member.welfare_last_refresh
                                  ? (
                                    <span title={`Last refresh: ${formatLocalDateTime(member.welfare_last_refresh)}`}>
                                      Last refresh: {formatRelativeTime(member.welfare_last_refresh)}
                                    </span>
                                  )
                                  : "Never refreshed"}
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRefreshWelfareSession(member.codice_persona)}
                                disabled={refreshingWelfareFor === member.codice_persona}
                                className="ml-auto h-6 w-6 shrink-0 rounded-md p-0 shadow-xs"
                                title="Refresh welfare session"
                              >
                                {refreshingWelfareFor === member.codice_persona ? (
                                  <RefreshCw className="h-3 w-3 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-3 w-3" />
                                )}
                              </Button>
                            </div>

                            {/* Token Consumption Status */}
                            <div className="flex w-full min-w-0 items-start gap-2 text-xs">
                              {(() => {
                                const memberConsumption = getMemberConsumption(member.codice_persona);
                                if (!memberConsumption) {
                                  return (
                                    <>
                                      <Clock className="h-3 w-3 shrink-0 text-gray-400" />
                                      <span className="min-w-0 flex-1 break-words text-gray-400">
                                        Loading consumption data...
                                      </span>
                                    </>
                                  );
                                }

                                if (memberConsumption.error) {
                                  return (
                                    <>
                                      <XCircle className="h-3 w-3 shrink-0 text-red-500" />
                                      <span className="min-w-0 flex-1 break-words text-red-500">Session error</span>
                                    </>
                                  );
                                }

                                if (!memberConsumption.sessionActive) {
                                  return (
                                    <>
                                      <XCircle className="h-3 w-3 shrink-0 text-red-500" />
                                      <span className="min-w-0 flex-1 break-words text-red-500">Session expired</span>
                                    </>
                                  );
                                }

                                if (memberConsumption.usedToday) {
                                  return (
                                    <>
                                      <CheckCircle className="h-3 w-3 shrink-0 text-green-600" />
                                      <span className="min-w-0 flex-1 break-words text-green-600">Token used today</span>
                                    </>
                                  );
                                } else {
                                  return (
                                    <>
                                      <ShoppingCart className="h-3 w-3 shrink-0 text-gray-500" />
                                      <span className="min-w-0 flex-1 break-words text-gray-500">No token used today</span>
                                    </>
                                  );
                                }
                              })()}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => fetchConsumptionData()}
                                disabled={refreshingConsumption}
                                className="ml-auto h-6 w-6 shrink-0 rounded-md p-0 shadow-xs"
                                title="Refresh consumption data"
                              >
                                {refreshingConsumption ? (
                                  <RefreshCw className="h-3 w-3 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-3 w-3" />
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
                        <Button
                          onClick={() => member.codice_persona && handleGenerateToken(member.codice_persona)}
                          disabled={
                            member.codice_persona ? isGeneratingToken === member.codice_persona.toString() : true
                          }
                          className="w-full sm:w-auto"
                        >
                          {member.codice_persona && isGeneratingToken === member.codice_persona.toString() ? (
                            <>
                              <RefreshCw className="h-4 w-4 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Key className="h-4 w-4" />
                              Generate Token
                            </>
                          )}
                        </Button>
                        {currentUserIsAdmin &&
                          member.codice_persona &&
                          member.codice_persona !== parseInt(user.codicePersona) && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="absolute right-3 top-3 h-8 w-8 rounded-md p-0 shadow-xs sm:static"
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {member.role !== "admin" && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedMember(member);
                                      setTransferModalOpen(true);
                                    }}
                                  >
                                    <Crown className="h-4 w-4 mr-2" />
                                    Make Admin
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => {
                                    setSelectedMember(member);
                                    setKickModalOpen(true);
                                  }}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <UserMinus className="h-4 w-4 mr-2" />
                                  Kick from Group
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Invite Management Modal */}
      {showInviteManagement && (
        <InviteManagement
          group={group}
          isOpen={showInviteManagement}
          onClose={() => setShowInviteManagement(false)}
          onInviteGenerated={fetchGroupMembers}
        />
      )}

      {/* Token Display Modal */}
      {tokenData && <TokenDisplayModal token={tokenData} isOpen={!!tokenData} onClose={() => setTokenData(null)} />}

      {/* Admin Action Modals */}
      <RenameGroupModal
        isOpen={renameModalOpen}
        onClose={() => setRenameModalOpen(false)}
        onConfirm={handleRenameGroup}
        groupName={groupName}
        isLoading={isRenamingGroup}
      />

      <KickMemberModal
        isOpen={kickModalOpen}
        onClose={() => {
          setKickModalOpen(false);
          setSelectedMember(null);
        }}
        onConfirm={handleKickMember}
        member={selectedMember}
        isLoading={isPerformingAdminAction}
      />

      <TransferAdminModal
        isOpen={transferModalOpen}
        onClose={() => {
          setTransferModalOpen(false);
          setSelectedMember(null);
        }}
        onConfirm={handleTransferAdmin}
        member={selectedMember}
        currentAdminName={user.displayName}
        isLoading={isPerformingAdminAction}
      />

      <DisbandGroupModal
        isOpen={disbandModalOpen}
        onClose={() => setDisbandModalOpen(false)}
        onConfirm={handleDisbandGroup}
        groupName={groupName}
        isLoading={isPerformingAdminAction}
      />

      <LeaveGroupModal
        isOpen={leaveModalOpen}
        onClose={() => setLeaveModalOpen(false)}
        onConfirm={handleLeaveGroupFromModal}
        groupName={groupName}
        isLoading={isLeavingGroup}
      />

      <ErrorModal
        isOpen={showErrorModal}
        onClose={handleCloseErrorModal}
        error={errorMessage}
        onRetry={retryAction || undefined}
        isRetrying={
          isLoading ||
          isGeneratingToken !== null ||
          isPerformingAdminAction ||
          isRenamingGroup ||
          refreshingWelfareFor !== null ||
          refreshingAllSessions ||
          refreshingConsumption
        }
      />
    </div>
  );
}
